import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  Fab,
  FormControlLabel,
  FormGroup,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import React from 'react'
import { blue, colors, spacingItem } from '../../../theme'
import { addPrimaryCtaSx, addSecondaryFabSx } from '../../../common/addPrimaryCtaSx'
import { useDialogOpened } from '../../../../utils/hooks/useDialogOpened'
import { LoadingButton } from '@mui/lab'
import { Controller, useForm } from 'react-hook-form'
import { useMutation } from 'react-query'
import {
  AnimalDocumentDtoType,
  UploadAnimalDocumentDto,
  UploadAnimalDocumentDtoAttributes,
} from '../../../../interactors/gen/backendClient'
import { useGlobalSnackbarStore } from '../../../../store/GlobalSnackBarStore'
import { useDropzone } from 'react-dropzone'
import { backendClient } from '../../../../interactors/clients/client'
import { Add, UploadFile } from '@mui/icons-material'
import { ControlledAutoCompleteWithCustomValue } from '../../../common/ControlledAutocompleteWithCustomValue'
import { getColorForAnimalDocumentType, getReadableAnimalDocumentType } from '../../../../domain/Animal/AnimalDisplay'
import { useAnimalStore } from '../../../../store/AnimalStore'
import { useCanEditAnimal } from '../../../../store/useCanEditAnimal'
import { isValidPastDateCoherence } from '../../../../utils/date/isValidPastDateCoherence'
import { ControlledDateField } from '../../../common/ControlledDateField'
import { forbiddenErrorMessage } from '../../../../utils/axios'

type ExtendedUploadAnimalDocumentDto = UploadAnimalDocumentDto & {
  requiredForRegisterEntry?: boolean
  requiredForRegisterExit?: boolean
  establishmentDate?: string
}

interface Props {
  animalId: string
  disabled?: boolean
  disabledReason?: string
}

export const AddDocumentModal: React.FC<Props> = ({ animalId, disabled, disabledReason }) => {
  const { isDialogOpen, openDialog, closeDialog } = useDialogOpened()
  const globalSnackbar = useGlobalSnackbarStore()
  const { getRootProps, getInputProps, isDragActive, acceptedFiles } = useDropzone()
  const animalStore = useAnimalStore()
  const canEditAnimal = useCanEditAnimal()

  const mutation = useMutation(
    async ({ data, file }: { data: ExtendedUploadAnimalDocumentDto; file: File }) => {
      const formData = new FormData()
      formData.append('file', file)
      if (data.type !== undefined) {
        formData.append('type', data.type)
      }

      if (data.name !== undefined) {
        formData.append('name', data.name)
      }

      if (data.reference !== undefined) {
        formData.append('reference', data.reference)
      }

      if (data.establishmentDate !== undefined) {
        formData.append('establishmentDate', data.establishmentDate)
      }

      data.attributes.forEach((value) => {
        formData.append('attributes', value)
      })
      return await backendClient.post(`animals/${animalId}/documents`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
    },
    {
      onSuccess: () => {
        closeDialog()
        globalSnackbar.triggerSuccessMessage(`Votre document a bien été ajouté !`)
        animalStore.changeSelectedAnimal(animalId)
      },
      onError: (error) => {
        // A 403 carries a message written for the user, e.g. the free plan's per-animal document limit.
        globalSnackbar.triggerErrorMessage(forbiddenErrorMessage(error) ?? `Une erreur est survenue !`)
      },
    }
  )

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    watch,
  } = useForm<ExtendedUploadAnimalDocumentDto>({
    defaultValues: {
      type: undefined,
      name: '',
      reference: '',
      requiredForRegisterEntry: false,
      requiredForRegisterExit: false,
      establishmentDate: undefined,
    },
    mode: 'onChange',
  })

  const onSubmit = (data: ExtendedUploadAnimalDocumentDto) => {
    const attributes = []
    if (data.requiredForRegisterEntry) {
      attributes.push(UploadAnimalDocumentDtoAttributes.Entry)
    }
    if (data.requiredForRegisterExit) {
      attributes.push(UploadAnimalDocumentDtoAttributes.Exit)
    }

    delete data.requiredForRegisterEntry
    delete data.requiredForRegisterExit

    const uploadData: UploadAnimalDocumentDto = {
      ...data,
      attributes: attributes as UploadAnimalDocumentDtoAttributes[],
    }

    if (acceptedFiles[0]) {
      mutation.mutate({ data: uploadData, file: acceptedFiles[0] })
    } else {
      globalSnackbar.triggerErrorMessage(`Vous devez ajouter un fichier !`)
    }
  }

  const type = watch('type')

  if (!canEditAnimal) {
    return null
  }

  return (
    <>
      <Tooltip title={disabled ? disabledReason || '' : ''} arrow disableHoverListener={!disabled}>
        <span>
          <Fab color="primary" variant="extended" onClick={openDialog} disabled={disabled} sx={addSecondaryFabSx}>
            <Add sx={{ mr: 1 }} />
            Ajouter un document
          </Fab>
        </span>
      </Tooltip>
      <Dialog
        open={isDialogOpen}
        onClose={closeDialog}
        PaperProps={{
          style: { borderRadius: 20, padding: 0, border: '1px solid #E4E8F2' },
        }}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent sx={{ p: 3 }}>
            <Box>
              <Typography
                variant="h4"
                component="h2"
                sx={{ marginBottom: 3, fontSize: '1.08rem', fontWeight: 800, color: colors.cello }}
              >
                Ajouter un document
              </Typography>

              <ControlledAutoCompleteWithCustomValue
                control={control}
                fieldName={'type'}
                options={Object.values(AnimalDocumentDtoType).map((type: AnimalDocumentDtoType) => ({
                  label: getReadableAnimalDocumentType(type),
                  value: type,
                  color: getColorForAnimalDocumentType(type),
                }))}
                label="Type de document"
                error={errors.type}
                requiredRule={'true'}
                defaultValue={''}
                size="small"
                chip={true}
              />

              {type === 'others' && (
                <TextField
                  label="Nom du document"
                  type="text"
                  fullWidth
                  size="small"
                  required
                  {...register('name', { required: 'Le nom est requis' })}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  sx={spacingItem}
                />
              )}
              {type === AnimalDocumentDtoType.VeterinaryCertificate && (
                <ControlledDateField
                  control={control}
                  validate={(value) => isValidPastDateCoherence(value) || !value}
                  fieldName={'establishmentDate'}
                  label="Date de l'établissement du certificat"
                  error={errors.establishmentDate}
                  requiredRule={"La date de l'établissement est requise"}
                  size="small"
                  sx={spacingItem}
                />
              )}
              <TextField
                label="Libellé"
                {...register('reference')}
                error={!!errors.reference}
                helperText={errors.reference?.message}
                type="text"
                fullWidth
                size="small"
                sx={spacingItem}
              />
              <FormGroup>
                <Controller
                  control={control}
                  name="requiredForRegisterEntry"
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!field.value}
                          onChange={(_event, checked) => field.onChange(checked)}
                          inputRef={field.ref}
                        />
                      }
                      label="Justificatif nécessaire au registre d'entrée"
                      sx={spacingItem}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="requiredForRegisterExit"
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!field.value}
                          onChange={(_event, checked) => field.onChange(checked)}
                          inputRef={field.ref}
                        />
                      }
                      label="Justificatif nécessaire au registre de sortie"
                    />
                  )}
                />
              </FormGroup>
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                padding={2.5}
                {...getRootProps({
                  sx: {
                    ...spacingItem,
                    border: '1.5px dashed',
                    borderRadius: 4,
                    borderStyle: 'dashed',
                    borderColor: isDragActive ? '#77A96B' : 'rgba(105, 141, 172, 0.38)',
                    backgroundColor: isDragActive
                      ? 'rgba(168, 213, 186, 0.2)'
                      : acceptedFiles[0]
                      ? '#F7F9FD'
                      : 'rgba(247, 249, 253, 0.92)',
                    transition: 'all 0.2s ease',
                    minHeight: 220,
                  },
                })}
              >
                <input {...getInputProps()} />
                <UploadFile sx={{ color: blue, fontSize: 40, mb: 1 }} />
                <Typography
                  variant="h4"
                  component="p"
                  sx={{ fontSize: 17, fontWeight: 800, color: colors.cello, textAlign: 'center' }}
                >
                  Glissez-déposer ou
                  <br />
                  Insérer un document
                </Typography>
                <Typography sx={{ mt: 0.75, fontSize: 12, fontWeight: 400, color: '#7A8197', textAlign: 'center' }}>
                  PDF, image ou pièce justificative
                </Typography>
                <Box
                  marginTop={2.5}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 1,
                    maxWidth: '100%',
                  }}
                >
                  <Typography variant="h4" component="p" fontSize={14} textAlign="center" sx={{ whiteSpace: 'nowrap' }}>
                    Votre document:
                  </Typography>
                  <Typography
                    variant="h4"
                    component="p"
                    fontSize={14}
                    textAlign="center"
                    color={colors.black}
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      flexGrow: 1,
                    }}
                  >
                    {acceptedFiles[0]?.name || 'Aucun fichier sélectionné'}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  sx={[
                    addPrimaryCtaSx,
                    { width: { xs: '100%', sm: '42%' }, mt: 1.25, borderRadius: '999px', minHeight: 38 },
                  ]}
                >
                  Parcourir
                </Button>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, pt: 0 }}>
            <Button
              data-testid="cancel"
              onClick={closeDialog}
              sx={{ textTransform: 'none', borderRadius: '999px', px: 2.25, fontWeight: 600 }}
            >
              Annuler
            </Button>
            <LoadingButton
              data-testid="apply-changes"
              variant="contained"
              type="submit"
              loading={mutation.isLoading}
              sx={[addPrimaryCtaSx, { borderRadius: '999px', minHeight: 38, px: 2.4 }]}
            >
              Valider
            </LoadingButton>
          </DialogActions>
        </form>
      </Dialog>
    </>
  )
}
